import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';

import { AppLayout } from './AppLayout';
import { ClientModeOutlet, ClientProtectedOutlet, TrainerProtectedOutlet } from './EmbedRouteGuards';
import AccountPage from '../widget-pages/account_AccountPage';
import AgreementsGatePage from '../widget-pages/auth_AgreementsGatePage';
import ConfirmEmailPage from '../widget-pages/auth_ConfirmEmailPage';
import { PATH } from '../widget-lib/path-routes';
import BookingsPage from '../widget-pages/booking_BookingsPage';
import BranchesPage from '../widget-pages/branch_BranchesPage';
import ClientIndexPage from '../widget-pages/landing_ClientIndexPage';
import ClientHubPage from '../widget-pages/landing_ClientHubPage';
import FitnessCorePage from '../widget-pages/account_FitnessCorePage';
import GlobalWidgetLandingPage from '../widget-pages/global_GlobalWidgetLandingPage';
import LegalCrmPage from '../widget-pages/legal_LegalCrmPage';
import LegalDocumentsOutlet from '../widget-pages/legal_LegalDocumentsOutlet';
import LegalOrganizationPage from '../widget-pages/legal_LegalOrganizationPage';
import LegalPrivacyPage from '../widget-pages/legal_LegalPrivacyPage';
import LegalRulesPage from '../widget-pages/legal_LegalRulesPage';
import MySubscriptionsPage from '../widget-pages/payment_MySubscriptionsPage';
import NotFoundPage from '../widget-pages/error_NotFoundPage';
import BookPage from '../widget-pages/schedule_BookPage';
import OrgAboutPage from '../widget-pages/org_OrgAboutPage';
import OrganizationsPage from '../widget-pages/branch_OrganizationsPage';
import PayCheckoutPage from '../widget-pages/payment_PayCheckoutPage';
import PayLoyaltyPage from '../widget-pages/payment_PayLoyaltyPage';
import BillingHistoryPage from '../widget-pages/payment_BillingHistoryPage';
import MyAccessPage from '../widget-pages/booking_MyAccessPage';
import ForgotPasswordPage from '../widget-pages/auth_ForgotPasswordPage';
import ResetPasswordPage from '../widget-pages/auth_ResetPasswordPage';

function RedirectToSignIn() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/sign-in', search: location.search }} replace />;
}
import ScheduleFiltersPage from '../widget-pages/schedule_ScheduleFiltersPage';
import SchedulePage from '../widget-pages/schedule_SchedulePage';
import ServiceDetailPage from '../widget-pages/service_ServiceDetailPage';
import ServicesListPage from '../widget-pages/service_ServicesListPage';
import SessionPage from '../widget-pages/schedule_SessionPage';
import SignInPage from '../widget-pages/auth_SignInPage';
import SubscriptionPlansPage from '../widget-pages/payment_SubscriptionPlansPage';
import TrainerDetailPage from '../widget-pages/trainer_TrainerDetailPage';
import TrainersListPage from '../widget-pages/trainer_TrainersListPage';
import TrainerBookClientPage from '../widget-pages/trainer_TrainerBookClientPage';
import TrainerBranchSelectPage from '../widget-pages/trainer_TrainerBranchSelectPage';
import { TrainerIndexRedirect } from './TrainerIndexRedirect';
import TrainerMySessionsPage from '../widget-pages/trainer_TrainerMySessionsPage';
import TrainerOrgSelectPage from '../widget-pages/trainer_TrainerOrgSelectPage';
import TrainerProfilePage from '../widget-pages/trainer_TrainerProfilePage';
import TrainerSignInPage from '../widget-pages/trainer_TrainerSignInPage';
import TrainerWorkSchedulePage from '../widget-pages/trainer_TrainerWorkSchedulePage';

/**
 * Дочерние сегменты без ведущего `/` — см. path-routes и frontend-embed-path-slugs.mdc.
 * Доступ: EmbedRouteGuards + GET /auth/me (ADR-0011). Продуктово: вход по ссылке с org, не «анонимный гость».
 * | Маршрут              | До JWT*     | Клиент JWT | Тренер (staff) |
 * |----------------------|-------------|------------|----------------|
 * | /, /schedule, /branches, /sessions/:id | без JWT: только при key/kid+sig (`canFetchWidget`), узкий просмотр | JWT: да | редирект /trainer |
 * | /book, /trainers, … | без JWT: вход; с JWT: да | да | редирект / |
 * | /sign-in, /register, /legal/*, 404 | публично (`handle.embedPublic`) | да | да |
 * | /trainer/sign-in     | да          | да         | да             |
 * | /trainer, /profile, /work-schedule, /my-sessions, /book-client | — | редирект / | да (после me)  |
 * *без JWT: только публичные маршруты или при key/kid+sig — см. `ClientProtectedOutlet`, провайдер.
 *
 * `basename` совпадает с `base` в Vite (`/embed/` в prod, `/` в dev).
 */
function embedBasename(): string {
  const raw = import.meta.env.BASE_URL;
  if (raw === '/') return '/';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

export const EMBED_ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        path: 'trainer',
        element: <Outlet />,
        children: [
          { path: 'sign-in', element: <TrainerSignInPage /> },
          { path: 'confirm-email', element: <ConfirmEmailPage afterVerifyPath={PATH.trainer} /> },
          { path: 'select-studio', element: <TrainerOrgSelectPage /> },
          {
            element: <TrainerProtectedOutlet />,
            children: [
              { index: true, element: <TrainerIndexRedirect /> },
              { path: 'select-branch', element: <TrainerBranchSelectPage /> },
              { path: 'profile', element: <TrainerProfilePage /> },
              { path: 'work-schedule', element: <TrainerWorkSchedulePage /> },
              { path: 'my-sessions', element: <TrainerMySessionsPage /> },
              { path: 'book-client', element: <TrainerBookClientPage /> }
            ]
          }
        ]
      },
      {
        element: <ClientModeOutlet />,
        children: [
          { path: 'catalog', element: <GlobalWidgetLandingPage />, handle: { embedPublic: true } },
          {
            element: <ClientProtectedOutlet />,
            children: [
              { index: true, element: <ClientIndexPage /> },
              { path: 'sign-in', element: <SignInPage />, handle: { embedPublic: true } },
              { path: 'register', element: <RedirectToSignIn />, handle: { embedPublic: true } },
              { path: 'forgot-password', element: <ForgotPasswordPage />, handle: { embedPublic: true } },
              { path: 'reset-password', element: <ResetPasswordPage />, handle: { embedPublic: true } },
              { path: 'agreements', element: <AgreementsGatePage /> },
              { path: 'confirm-email', element: <ConfirmEmailPage /> },
              { path: 'schedule/filters', element: <ScheduleFiltersPage /> },
              { path: 'schedule', element: <SchedulePage /> },
              { path: 'app', element: <ClientHubPage /> },
              { path: 'book', element: <BookPage /> },
              { path: 'pay/loyalty', element: <PayLoyaltyPage /> },
              { path: 'pay/checkout', element: <PayCheckoutPage /> },
              { path: 'billing', element: <BillingHistoryPage /> },
              { path: 'my-access', element: <MyAccessPage /> },
              { path: 'trainers', element: <TrainersListPage /> },
              { path: 'trainers/:trainerId', element: <TrainerDetailPage /> },
              { path: 'services', element: <ServicesListPage /> },
              { path: 'services/:serviceId', element: <ServiceDetailPage /> },
              { path: 'about', element: <OrgAboutPage /> },
              { path: 'account', element: <AccountPage /> },
              { path: 'branches', element: <BranchesPage /> },
              { path: 'sessions/:slotId', element: <SessionPage /> },
              { path: 'fitness', element: <FitnessCorePage /> },
              { path: 'bookings', element: <BookingsPage /> },
              { path: 'subscription-plans', element: <SubscriptionPlansPage /> },
              { path: 'my-subscriptions', element: <MySubscriptionsPage /> },
              { path: 'organizations', element: <OrganizationsPage /> },
              {
                path: 'legal',
                element: <LegalDocumentsOutlet />,
                handle: { embedPublic: true },
                children: [
                  { path: 'privacy', element: <LegalPrivacyPage /> },
                  { path: 'crm', element: <LegalCrmPage /> },
                  { path: 'rules', element: <LegalRulesPage /> },
                  { path: 'organization', element: <LegalOrganizationPage /> }
                ]
              },
              { path: '*', element: <NotFoundPage />, handle: { embedPublic: true } }
            ]
          }
        ]
      }
    ]
  }
];

export const router = createBrowserRouter(
  EMBED_ROUTES,
  {
    basename: embedBasename(),

  }
);
